import { Component, OnInit, OnDestroy, OnChanges, SimpleChanges, Inject, Input, ChangeDetectorRef, ViewChild, ElementRef } from '@angular/core';
import { ReportService } from '../../services/report.service';
import { ReportComponent } from '../../report.component';
import { Subscription } from 'rxjs';
import { DOCUMENT } from '@angular/common';

@Component({
    selector: 'app-ai-sentence-analysis',
    templateUrl: './ai-sentence-analysis.component.html',
    styleUrls: ['./ai-sentence-analysis.component.scss']
})
export class AiSentenceAnalysisComponent implements OnInit, OnChanges, OnDestroy {
    @Input() sentenceDataInput: any = null; // optional input when used embedded
    @Input() embedded: boolean = false;     // indicates usage inside another view (e.g. AI Authorship tab)

    @ViewChild('analysisTitle') analysisTitle!: ElementRef<HTMLElement>;

    sentenceData: any = null;
    private subscription: Subscription = new Subscription();

    constructor(
        private reportService: ReportService,
        private reportComponent: ReportComponent,
        @Inject(DOCUMENT) private document: Document,
        private cdr: ChangeDetectorRef
    ) { }

    ngOnInit(): void {
        // If sentence data is passed in directly (embedded mode), use that and skip service subscription
        if (this.sentenceDataInput) {
            this.sentenceData = this.enrichSegmentData({ ...this.sentenceDataInput });
        } else {
            this.subscription.add(
                this.reportService.aiSentenceAnalysisObject.subscribe(data => {
                    this.sentenceData = data ? this.enrichSegmentData({ ...data }) : null;
                    this.cdr.detectChanges();
                    if (data) {
                        setTimeout(() => {
                            (this.document.activeElement as HTMLElement)?.blur?.();
                            const title = this.analysisTitle?.nativeElement
                                ?? this.document.querySelector('app-ai-sentence-analysis h3[tabindex="-1"]');
                            (title as HTMLElement)?.focus({ preventScroll: true });
                        }, 0);
                    }
                })
            );
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['sentenceDataInput'] && this.sentenceDataInput) {
            this.sentenceData = this.enrichSegmentData({ ...this.sentenceDataInput });
            this.cdr.detectChanges();
        }
    }

    ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    close(): void {
        const data = this.sentenceData;
        this.removeAISentenceUnderline();
        this.reportService.aiSentenceAnalysisSubject.next(null);

        setTimeout(() => {
            if (!data) {
                return;
            }

            let el: HTMLElement | null = null;
            if (data.windowIndex !== undefined) {
                el = document.querySelector(`[data-window-index="${data.windowIndex}"][tabindex="0"]`) as HTMLElement
                    ?? document.querySelector(`[data-window-index="${data.windowIndex}"]`) as HTMLElement;
            }
            if (!el && data.id != null) {
                el = document.querySelector(`[sentence-id="${data.id}"][tabindex="0"]`) as HTMLElement
                    ?? document.querySelector(`[sentence-id="${data.id}"]`) as HTMLElement;
            }

            el?.focus({ preventScroll: true });
        }, 0);
    }

    removeAISentenceUnderline(): void {
        // Remove underline from all previously selected sentences
        const selectedElements = this.document.querySelectorAll('[data-ai-selected="true"]');
        selectedElements.forEach((element: any) => {
            element.style.borderBottom = '';
            element.style.borderBottomLeftRadius = '';
            element.style.borderBottomRightRadius = '';
            element.removeAttribute('data-ai-selected');
        });
    }

    getSentenceText(): string {
        if (this.sentenceData) {

            if (this.sentenceData.windowText) {
                return this.sentenceData.windowText;
            }

            const resolvedAiSentences = this.resolveAiSentences(this.sentenceData);
            if (resolvedAiSentences) {

                let aiSentence: any = null;

                if (this.sentenceData.windowIndex !== undefined &&
                    this.sentenceData.windowIndex >= 0 &&
                    this.sentenceData.windowIndex < resolvedAiSentences.length) {
                    aiSentence = resolvedAiSentences[this.sentenceData.windowIndex];
                }

                if (!aiSentence && this.sentenceData.text) {
                    const sentenceText = this.sentenceData.text.trim();
                    aiSentence = resolvedAiSentences.find((segment: any) =>
                        segment.text?.trim() === sentenceText ||
                        segment.text?.includes(sentenceText) ||
                        sentenceText.includes(segment.text?.trim() || '')
                    );
                }

                if (!aiSentence) {
                    aiSentence = resolvedAiSentences.find((segment: any) =>
                        segment.label === 'AI-Generated'
                    );
                }

                if (aiSentence && aiSentence.text) {
                    return aiSentence.text;
                }
            }

            const text = this.sentenceData.text || this.sentenceData.sentence || '';
            return text;
        }

        return '';
    }

    isWindowData(): boolean {
        if (!this.sentenceData) return false;

        if (this.sentenceData.isWindow === true || this.sentenceData.hasWindowData === true) {
            return true;
        }

        const isWindowAiSentences = this.resolveAiSentences(this.sentenceData);
        if (isWindowAiSentences && isWindowAiSentences.length > 0) {
            return true;
        }

        if (this.sentenceData.windowText || this.sentenceData.windowIndex !== undefined) {
            return true;
        }

        return false;
    }

    getAIConfidenceFromWindow(): string {
        if (!this.sentenceData) return '0.0';

        const confidenceAiSentences = this.resolveAiSentences(this.sentenceData);
        if (confidenceAiSentences) {

            let aiSentence: any = null;

            if (this.sentenceData.windowIndex !== undefined &&
                this.sentenceData.windowIndex >= 0 &&
                this.sentenceData.windowIndex < confidenceAiSentences.length) {
                aiSentence = confidenceAiSentences[this.sentenceData.windowIndex];
            }

            if (!aiSentence && this.sentenceData.windowText) {
                aiSentence = confidenceAiSentences.find((segment: any) =>
                    segment.text === this.sentenceData.windowText ||
                    segment.text?.trim() === this.sentenceData.windowText?.trim()
                );
            }

            if (!aiSentence && this.sentenceData.text) {
                const sentenceText = this.sentenceData.text.trim();
                aiSentence = confidenceAiSentences.find((segment: any) =>
                    segment.text?.trim() === sentenceText ||
                    segment.text?.includes(sentenceText) ||
                    sentenceText.includes(segment.text?.trim() || '')
                );
            }

            if (!aiSentence) {
                aiSentence = confidenceAiSentences.find((segment: any) =>
                    segment.label === 'AI-Generated'
                );
            }

            if (aiSentence && aiSentence.ai_assistance_score !== undefined) {
                return (aiSentence.ai_assistance_score * 100).toFixed(1);
            }
        }

        return '0.0';
    }

    getWordCountFromWindow(): number {
        if (!this.sentenceData) return 0;

        const wordCountAiSentences = this.resolveAiSentences(this.sentenceData);
        if (wordCountAiSentences) {

            let aiSentence: any = null;

            if (this.sentenceData.windowIndex !== undefined &&
                this.sentenceData.windowIndex >= 0 &&
                this.sentenceData.windowIndex < wordCountAiSentences.length) {
                aiSentence = wordCountAiSentences[this.sentenceData.windowIndex];
            }

            if (!aiSentence && this.sentenceData.windowText) {
                aiSentence = wordCountAiSentences.find((segment: any) =>
                    segment.text === this.sentenceData.windowText ||
                    segment.text?.trim() === this.sentenceData.windowText?.trim()
                );
            }

            if (!aiSentence && this.sentenceData.text) {
                const sentenceText = this.sentenceData.text.trim();
                aiSentence = wordCountAiSentences.find((segment: any) =>
                    segment.text?.trim() === sentenceText ||
                    segment.text?.includes(sentenceText) ||
                    sentenceText.includes(segment.text?.trim() || '')
                );
            }

            if (!aiSentence) {
                aiSentence = wordCountAiSentences.find((segment: any) =>
                    segment.label === 'AI-Generated'
                );
            }

            if (aiSentence && aiSentence.word_count !== undefined) {
                return aiSentence.word_count;
            }
        }

        return 0;
    }

    getHighlightedWindowText(): string {
        const text = this.getSentenceText();
        if (!text) {
            return '';
        }
        const sentences = text.split(/([.!?]+\s+)/);
        const highlightedSentences = sentences.map((sentence, index) => {
            if (sentence.trim()) {
                if (/^[.!?]+\s*$/.test(sentence)) {
                    return sentence;
                }
                return `<span class="ai-sentence-highlight">${sentence}</span>`;
            }
            return sentence;
        });
        return highlightedSentences.join('');
    }

    getHighlightedText(text: string, keywords: any[]): string {
        if (!text) {
            return '';
        }
        // Create continuous highlight like sentence-information does
        let result = `<em class="ai-sentence-highlight">${text}</em>`;

        // Then add keyword highlighting on top (like sentence-information adds em tags)
        if (keywords && keywords.length > 0) {
            keywords.forEach(keywordObj => {
                const keyword = keywordObj.keyword;
                const escapedKeyword = keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                const regex = new RegExp(`\\b(${escapedKeyword})\\b`, 'gi');
                result = result.replace(regex, '<span class="ai-keyword-deep-highlight">$1</span>');
            });
        }

        return result;
    }

    /**
     * Resolves the ai_sentences array from a data object.
     * Supports new structure (ai_sentences at the same level as pangramResults)
     * with backwards compatibility for the old pangramResults.ai_sentences path.
     */
    private resolveAiSentences(data: any): any[] | null {
        if (Array.isArray(data?.ai_sentences)) return data.ai_sentences;
        if (Array.isArray(data?.pangramResults?.ai_sentences)) return data.pangramResults.ai_sentences;
        return null;
    }

    private normalizeWindowIndex(value: any): number | null {
        if (value === undefined || value === null || value === '') {
            return null;
        }
        const parsed = Number(value);
        return Number.isNaN(parsed) ? null : parsed;
    }

    private normalizeSegmentText(text?: string): string {
        return (text || '').replace(/\s+/g, ' ').trim();
    }

    private enrichSegmentData(data: any): any {
        if (!data) {
            return null;
        }

        const enriched = { ...data };
        const aiSentences = this.resolveAiSentences(enriched);

        if (!enriched.windowText && Array.isArray(aiSentences)) {
            const idx = this.normalizeWindowIndex(enriched.windowIndex);
            if (idx !== null && aiSentences[idx]?.text) {
                enriched.windowText = aiSentences[idx].text;
            }
        }

        if (this.normalizeWindowIndex(enriched.windowIndex) === null && Array.isArray(aiSentences)) {
            const activeText = this.normalizeSegmentText(enriched.windowText || enriched.text);
            if (activeText) {
                const idx = aiSentences.findIndex((segment: any) => {
                    const segmentText = this.normalizeSegmentText(segment.text);
                    return segmentText === activeText ||
                        segmentText.includes(activeText) ||
                        activeText.includes(segmentText);
                });
                if (idx >= 0) {
                    enriched.windowIndex = idx;
                }
            }
        }

        enriched.hasWindowData = true;
        enriched.isWindow = true;
        return enriched;
    }

    private getActiveSegmentText(data: any): string {
        if (!data) {
            return '';
        }

        const fromWindowText = this.normalizeSegmentText(data.windowText);
        if (fromWindowText) {
            return fromWindowText;
        }

        const windowIndex = this.normalizeWindowIndex(data.windowIndex);
        const activeAiSentences = this.resolveAiSentences(data);
        if (windowIndex !== null && activeAiSentences) {
            return this.normalizeSegmentText(activeAiSentences[windowIndex]?.text);
        }

        return this.normalizeSegmentText(data.text);
    }

    private segmentMatchesActive(segment: any, activeText: string, activeWindowIndex: number | null): boolean {
        const segmentText = this.getActiveSegmentText(segment);
        if (activeText && segmentText && (
            segmentText === activeText ||
            segmentText.includes(activeText) ||
            activeText.includes(segmentText)
        )) {
            return true;
        }

        const segmentWindowIndex = this.normalizeWindowIndex(segment.windowIndex);
        return activeWindowIndex !== null &&
            segmentWindowIndex !== null &&
            segmentWindowIndex === activeWindowIndex;
    }

    private buildSegmentData(sentence: any, windowIndex: number): any {
        const segmentAiSentences = this.resolveAiSentences(sentence);
        const windowData = segmentAiSentences?.[windowIndex];
        if (!windowData) {
            return null;
        }

        return {
            ...sentence,
            windowIndex,
            windowText: windowData.text,
            windowLabel: windowData.label,
            windowConfidence: windowData.confidence,
            windowAiAssistanceScore: windowData.ai_assistance_score,
            windowStartIndex: windowData.start_index,
            windowEndIndex: windowData.end_index,
            windowWordCount: windowData.word_count,
            windowTokenLength: windowData.token_length,
            hasWindowData: true,
            isWindow: true,
        };
    }

    private get aiSentences(): any[] {
        return (this.reportComponent?.sentencesWithAI as any[]) ?? [];
    }

    get allSegments(): any[] {
        const segments: any[] = [];
        const seen = new Set<string>();

        for (const sentence of this.aiSentences) {
            const aiSentences = this.resolveAiSentences(sentence);
            if (!Array.isArray(aiSentences)) {
                continue;
            }

            for (let windowIndex = 0; windowIndex < aiSentences.length; windowIndex++) {
                const window = aiSentences[windowIndex];
                if (window.label !== 'AI-Generated') {
                    continue;
                }

                if (!window.text || !sentence.text || !window.text.includes(sentence.text.trim())) {
                    continue;
                }

                const key = this.normalizeSegmentText(window.text);
                if (!key || seen.has(key)) {
                    continue;
                }
                seen.add(key);

                const segmentData = this.buildSegmentData(sentence, windowIndex);
                if (segmentData) {
                    segments.push(segmentData);
                }
            }
        }

        return segments.sort((a, b) => {
            const startA = a.windowStartIndex ?? Number.MAX_SAFE_INTEGER;
            const startB = b.windowStartIndex ?? Number.MAX_SAFE_INTEGER;
            if (startA !== startB) {
                return startA - startB;
            }
            if (a.page !== b.page) {
                return a.page - b.page;
            }
            return (this.normalizeWindowIndex(a.windowIndex) ?? 0) - (this.normalizeWindowIndex(b.windowIndex) ?? 0);
        });
    }

    get currentSegmentIndex(): number {
        if (!this.sentenceData || !this.allSegments.length) {
            return -1;
        }

        const activeText = this.getActiveSegmentText(this.sentenceData);
        const activeWindowIndex = this.normalizeWindowIndex(this.sentenceData.windowIndex);

        const index = this.allSegments.findIndex(segment =>
            this.segmentMatchesActive(segment, activeText, activeWindowIndex)
        );

        if (index >= 0) {
            return index;
        }

        return this.allSegments.findIndex(segment =>
            String(segment.id) === String(this.sentenceData.id)
        );
    }

    get totalSegments(): number {
        return this.allSegments.length;
    }

    get hasPreviousSegment(): boolean {
        return this.currentSegmentIndex > 0;
    }

    get hasNextSegment(): boolean {
        const idx = this.currentSegmentIndex;
        return idx >= 0 && idx < this.totalSegments - 1;
    }

    navigateSegment(direction: 'previous' | 'next'): void {
        const idx = this.currentSegmentIndex;
        if (idx === -1) {
            return;
        }

        const nextIdx = direction === 'next' ? idx + 1 : idx - 1;
        const segment = this.allSegments[nextIdx];
        if (!segment) {
            return;
        }

        this.reportService.activateAISegmentSubject.next({
            id: segment.id,
            page: segment.page,
            windowIndex: segment.windowIndex,
            segment,
        });
    }
}